/**
 * Guided Tours - Interactive Step-by-Step Tutorials
 *
 * These tours highlight UI elements and wait for user actions.
 * Each step can:
 * - Highlight a specific element
 * - Wait for user to click/interact
 * - Show contextual instructions
 * - Auto-advance when action is completed
 */

export const GUIDED_TOURS = {
  // ============================================
  // TRADE ROUTES TOUR
  // ============================================
  tradeRoutes: {
    id: 'tradeRoutes',
    title: 'Trade Routes Guide',
    description: 'Learn how to connect tiles to your Kingdom with trade routes.',
    // Queue modals to open when tour completes or at specific steps
    queuedModals: [],
    steps: [
      {
        id: 'intro',
        title: 'Trade Routes 101',
        content: 'Trade routes connect tiles to your Kingdom, unlocking **building capacity** in the Buildings Panel.\n\n**Key Benefit:** Each tile building grants **+5 panel capacity** for that building type, but only when **connected via trade route**!\n\nLet\'s learn how to build them!',
        position: 'center',
        targetElement: null,
        waitForAction: null, // null = just click "Next" to continue
        actionLabel: 'Next',
      },
      {
        id: 'find_toggle',
        title: 'Step 1: Find the Toggle',
        content: 'Look for the **🛤️ Trade Route** bar below the exploration limit. This toggles the trade route overlay.',
        position: 'bottom-right',
        targetElement: '.wm-trade-route-bar',
        waitForAction: null,
        actionLabel: 'I see it',
        highlight: true,
      },
      {
        id: 'toggle_overlay',
        title: 'Step 2: Toggle the Overlay',
        content: 'Click the **🛤️ Trade Route** bar to turn on the overlay. You\'ll see golden lines showing existing routes.',
        position: 'bottom-right',
        targetElement: '.wm-trade-route-bar',
        waitForAction: 'tradeRouteOverlayOn',
        actionLabel: 'Click the bar',
        highlight: true,
        pulseAnimation: true,
      },
      {
        id: 'understand_overlay',
        title: 'Understanding the Overlay',
        content: 'When the overlay is **ON**:\n\n• **Bright tiles** = Connected to Kingdom\n• **Dim tiles** = Not connected (buildings won\'t grant panel capacity!)\n• **Golden lines** = Trade route paths flowing toward your Kingdom\n\nRoutes must connect back to your Kingdom (0,0)!',
        position: 'center',
        targetElement: null,
        waitForAction: null,
        actionLabel: 'Got it',
      },
      {
        id: 'find_build_section',
        title: 'Step 3: Find a Tile to Connect',
        content: 'Go to any tile **adjacent to your Kingdom** or an **existing trade route**. Look for the **🛤️ Build Trade Route** section in the tile info.',
        position: 'bottom',
        targetElement: '.wm-trade-route-section',
        waitForAction: null,
        actionLabel: 'I found it',
        highlight: true,
        fallbackContent: 'Move to a tile near your Kingdom first, then you\'ll see the Build Trade Route button.',
      },
      {
        id: 'build_route',
        title: 'Step 4: Build a Route',
        content: 'Click **Build Route** to create a trade route segment on this tile. It costs resources based on distance from your Kingdom.',
        position: 'bottom',
        targetElement: '.wm-build-route-btn',
        waitForAction: 'tradeRouteBuilt',
        actionLabel: 'Build a route',
        highlight: true,
        pulseAnimation: true,
        skipIfMissing: true, // Skip if not at a valid tile
      },
      {
        id: 'success',
        title: 'Trade Route Built!',
        content: 'Excellent! You built your first trade route segment.\n\n**Key Points:**\n• Build routes on **adjacent tiles** to extend them\n• Connected tile buildings grant **+5 panel capacity** each\n• **Unconnected** tile buildings don\'t count toward capacity!\n• Research techs to **reduce route costs**\n• Build outposts and connect them for maximum benefit!',
        position: 'center',
        targetElement: null,
        waitForAction: null,
        actionLabel: 'Finish Tour',
        isComplete: true,
      },
    ],
  },

  // ============================================
  // OUTPOSTS TOUR
  // ============================================
  outposts: {
    id: 'outposts',
    title: 'Outposts Guide',
    description: 'Learn how to create outposts and use mercenaries.',
    // Don't open modal until step 2 is completed
    deferModalUntilStep: 'click_create',
    steps: [
      {
        id: 'intro',
        title: 'Outposts Overview',
        content: 'Outposts are **player-built settlements** on the world map.\n\n**Benefits:**\n• **4 building slots** (regular tiles have 1)\n• **Mercenary hiring** for auto-dungeon runs\n• **Instant teleportation** from anywhere\n• **Trade route hub** for bonuses',
        position: 'center',
        targetElement: null,
        waitForAction: null,
        actionLabel: 'Next',
        closeModal: true, // Ensure modal is closed for intro
      },
      {
        id: 'find_button',
        title: 'Find the Create Button',
        content: 'Look for the **🏰 Create Outpost** button in the side panel. It appears on regular tiles (not dungeons or special biomes).',
        position: 'bottom',
        targetElement: '.wm-create-outpost-btn',
        waitForAction: null,
        actionLabel: 'I see it',
        highlight: true,
        fallbackContent: 'The Create Outpost button appears in the tile info panel on regular tiles. Move to a forest, plains, or mountain tile to see it.',
        closeModal: true, // Keep modal closed
      },
      {
        id: 'click_create',
        title: 'Click Create Outpost',
        content: 'Click the **🏰 Create Outpost** button to open the creation modal.',
        position: 'bottom',
        targetElement: '.wm-create-outpost-btn',
        waitForAction: 'outpostModalOpened',
        actionLabel: 'Click the button',
        highlight: true,
        pulseAnimation: true,
        skipIfMissing: true,
        closeModal: true, // Keep modal closed until action completed
        openModalOnComplete: 'outpostCreation', // Signal to open modal after this step
      },
      {
        id: 'modal_overview',
        title: 'Creation Modal',
        content: 'Here you can:\n\n• **Name your outpost** - Give it a custom name\n• **See the cost** - Resources needed (varies by biome)\n• **View features** - 4 slots, mercs, teleport, trade route\n\nCosts vary by biome tier. Forest/Plains are cheapest!',
        position: 'sidebar',
        targetElement: '.ocm-modal',
        waitForAction: null,
        actionLabel: 'Next',
        highlight: true,
        requiresModal: 'outpostCreation', // This step needs the modal open
      },
      {
        id: 'name_outpost',
        title: 'Name Your Outpost',
        content: 'Enter a name for your outpost. This will appear on the map and in your outpost list.',
        position: 'sidebar',
        targetElement: '.ocm-name-input',
        waitForAction: null,
        actionLabel: 'Named it',
        highlight: true,
      },
      {
        id: 'confirm_creation',
        title: 'Confirm Creation',
        content: 'Click **Create Outpost** to start construction. It takes **30 seconds** (construction continues even when offline!).',
        position: 'sidebar',
        targetElement: '.ocm-create-btn',
        waitForAction: 'outpostCreated',
        actionLabel: 'Create it',
        highlight: true,
        pulseAnimation: true,
        skipIfMissing: true,
      },
      {
        id: 'construction',
        title: 'Construction Started!',
        content: 'Your outpost is under construction! You\'ll see a **🏗️** icon on the map.\n\nOnce complete, click it to open the **Outpost Management** modal with 3 tabs:\n• **Buildings** - Build up to 4 buildings\n• **Mercenaries** - Hire warriors\n• **Missions** - Send squads to dungeons',
        position: 'sidebar',
        targetElement: null,
        waitForAction: null,
        actionLabel: 'Next',
      },
      {
        id: 'buildings_tab',
        title: 'Buildings Tab',
        content: 'Each outpost has **4 building slots**. Each building grants **+5 panel capacity** for that type.\n\n**Example:** 2 Lumber Mills in outpost = can buy 10 more in Buildings Panel!',
        position: 'sidebar',
        targetElement: null,
        waitForAction: null,
        actionLabel: 'Next',
      },
      {
        id: 'mercenaries_tab',
        title: 'Mercenaries Tab',
        content: '**4 Classes:**\n• ⚔️ Warrior - Tank, high HP\n• 🏹 Ranger - DPS, good crit\n• 🔮 Mage - Magic damage\n• 🗡️ Rogue - Burst damage, highest crit\n\nMercs have **quality ratings** like equipment (0.6x-1.4x stats).',
        position: 'sidebar',
        targetElement: null,
        waitForAction: null,
        actionLabel: 'Next',
      },
      {
        id: 'missions_tab',
        title: 'Missions Tab',
        content: '**Squad Missions** let mercs auto-run dungeons!\n\n1. Form squads (1-4 mercs)\n2. Select dungeon tier\n3. Start mission\n4. Collect rewards when done\n\n**Rewards:** 70% of normal dungeon loot.',
        position: 'sidebar',
        targetElement: null,
        waitForAction: null,
        actionLabel: 'Next',
      },
      {
        id: 'warning',
        title: 'Important Warning!',
        content: '**Failed missions have consequences!**\n\n• **30% death chance** per merc on failure\n• Dead mercs are **gone forever** (permadeath)\n\n**Tip:** Don\'t send weak squads to hard dungeons. Match squad power to dungeon difficulty!',
        position: 'sidebar',
        targetElement: null,
        waitForAction: null,
        actionLabel: 'Next',
        isWarning: true,
      },
      {
        id: 'success',
        title: 'Outposts Mastered!',
        content: '**You\'re ready to use Outposts!**\n\n**Quick Tips:**\n• Build near dungeon clusters for efficient merc farming\n• Connect via trade routes for bonuses\n• Use teleportation for fast travel\n• Max 3 outposts (expandable later)',
        position: 'sidebar',
        targetElement: null,
        waitForAction: null,
        actionLabel: 'Finish Tour',
        isComplete: true,
      },
    ],
  },
};

/**
 * Guided Tour configuration
 */
export const GUIDED_TOUR_CONFIG = {
  // Animation durations
  fadeInDuration: 250,
  fadeOutDuration: 200,
  highlightPulseDuration: 1500,

  // Overlay settings
  overlayColor: 'rgba(0, 0, 0, 0.75)',
  highlightPadding: 8,
  highlightBorderRadius: 8,

  // Card settings
  cardMaxWidth: 420,
  arrowSize: 10,

  // Behavior
  allowSkip: true,
  allowClose: true,
  showProgress: true,
};

/**
 * Get a guided tour by ID
 */
export function getGuidedTour(id) {
  return GUIDED_TOURS[id] || null;
}

/**
 * Get all guided tour IDs
 */
export function getAllGuidedTourIds() {
  return Object.keys(GUIDED_TOURS);
}
